import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import type { TajweedRule, RuleTrigger, Finding } from '../types';
import clsx from 'clsx';

const RECITERS: { id: string; name: string; folder: string }[] = [
  { id: 'husary', name: 'Al-Husary', folder: 'Husary_128kbps' },
  { id: 'minshawi_murattal', name: 'Al-Minshawi', folder: 'Minshawy_Murattal_128kbps' },
  { id: 'abdulbasit', name: 'Abdul Basit', folder: 'Abdul_Basit_Murattal_192kbps' },
];

function buildAudioUrl(folder: string, surah: number, ayah: number): string {
  const s = String(surah).padStart(3, '0');
  const a = String(ayah).padStart(3, '0');
  return `https://everyayah.com/data/${folder}/${s}${a}.mp3`;
}

// Phonetic rule categories (makhraj, sifat, etc.) come from the audio-LLM eval
// rather than the static rule-trigger engine, so they don't need their own
// highlight CSS — they reuse the gray "neutral" treatment.
const RULE_HIGHLIGHT_CLASS: Record<TajweedRule, string> = {
  madd: 'arabic-highlight-madd',
  ghunnah: 'arabic-highlight-ghunnah',
  idgham: 'arabic-highlight-idgham',
  ikhfa: 'arabic-highlight-ikhfa',
  iqlab: 'arabic-highlight-iqlab',
  qalqalah: 'arabic-highlight-qalqalah',
  tafkheem_tarqeeq: 'arabic-highlight-madd',
  waqf_ibtida: 'arabic-highlight-ghunnah',
  makhraj: 'arabic-highlight-madd',
  sifat: 'arabic-highlight-ghunnah',
  tajweed_rule: 'arabic-highlight-madd',
  timing: 'arabic-highlight-madd',
  pronunciation: 'arabic-highlight-madd',
};

const RULE_LEGEND_COLOR: Record<TajweedRule, string> = {
  madd: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-500/20 dark:text-blue-300 dark:border-blue-500/30',
  ghunnah: 'bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-500/20 dark:text-purple-300 dark:border-purple-500/30',
  idgham: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-500/20 dark:text-amber-300 dark:border-amber-500/30',
  ikhfa: 'bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-500/20 dark:text-orange-300 dark:border-orange-500/30',
  iqlab: 'bg-teal-100 text-teal-700 border-teal-300 dark:bg-teal-500/20 dark:text-teal-300 dark:border-teal-500/30',
  qalqalah: 'bg-green-100 text-green-700 border-green-300 dark:bg-green-500/20 dark:text-green-300 dark:border-green-500/30',
  tafkheem_tarqeeq: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-500/20 dark:text-blue-300 dark:border-blue-500/30',
  waqf_ibtida: 'bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-500/20 dark:text-purple-300 dark:border-purple-500/30',
  makhraj: 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-500/20 dark:text-gray-300 dark:border-gray-500/30',
  sifat: 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-500/20 dark:text-gray-300 dark:border-gray-500/30',
  tajweed_rule: 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-500/20 dark:text-gray-300 dark:border-gray-500/30',
  timing: 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-500/20 dark:text-gray-300 dark:border-gray-500/30',
  pronunciation: 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-500/20 dark:text-gray-300 dark:border-gray-500/30',
};

const RULE_LABEL: Record<TajweedRule, string> = {
  madd: 'Madd',
  ghunnah: 'Ghunnah',
  idgham: 'Idgham',
  ikhfa: 'Ikhfa',
  iqlab: 'Iqlab',
  qalqalah: 'Qalqalah',
  tafkheem_tarqeeq: 'Tafkheem/Tarqeeq',
  waqf_ibtida: 'Waqf/Ibtida',
  makhraj: 'Makhraj',
  sifat: 'Sifat',
  tajweed_rule: 'Tajweed Rule',
  timing: 'Timing',
  pronunciation: 'Pronunciation',
};

// ── Word boundary mapping ──────────────────────────────────────────
interface WordBoundary {
  word: string;
  startIndex: number;
  endIndex: number; // exclusive
}

function splitIntoWords(text: string): WordBoundary[] {
  const words: WordBoundary[] = [];
  const regex = /\S+/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    words.push({
      word: match[0],
      startIndex: match.index,
      endIndex: match.index + match[0].length,
    });
  }
  return words;
}

// ── Character-level class builder (tajweed + findings + karaoke) ──
interface CharClass {
  char: string;
  tajweedClass: string;
  tooltip: string;
  wordIndex: number; // which word this char belongs to (-1 for whitespace)
}

function buildCharClasses(
  text: string,
  ruleTriggers: RuleTrigger[],
  findings: Finding[],
  words: WordBoundary[]
): CharClass[] {
  // Map each char index to its word index
  const charToWord = new Int16Array(text.length).fill(-1);
  for (let wi = 0; wi < words.length; wi++) {
    for (let ci = words[wi].startIndex; ci < words[wi].endIndex; ci++) {
      charToWord[ci] = wi;
    }
  }

  const classes: CharClass[] = text.split('').map((char, i) => ({
    char,
    tajweedClass: '',
    tooltip: '',
    wordIndex: charToWord[i],
  }));

  // Layer 1: tajweed rule triggers
  for (const trigger of ruleTriggers) {
    for (let i = trigger.startIndex; i < trigger.endIndex && i < classes.length; i++) {
      if (!classes[i].tajweedClass) {
        classes[i].tajweedClass = RULE_HIGHLIGHT_CLASS[trigger.rule] || '';
      }
      if (!classes[i].tooltip) {
        classes[i].tooltip = trigger.description;
      }
    }
  }

  // Layer 2: evaluation findings (override)
  for (const finding of findings) {
    for (const span of finding.spans) {
      for (let i = span.startIndex; i < span.endIndex && i < classes.length; i++) {
        classes[i].tajweedClass = 'arabic-highlight-error';
        classes[i].tooltip = finding.explanationForUser;
      }
    }
  }

  return classes;
}

// ── Group consecutive chars with same class + word into spans ──
interface WordSpan {
  text: string;
  tajweedClass: string;
  tooltip: string;
  wordIndex: number;
}

function groupIntoSpans(charClasses: CharClass[]): WordSpan[] {
  if (charClasses.length === 0) return [];

  const spans: WordSpan[] = [];
  let cur: WordSpan = {
    text: charClasses[0].char,
    tajweedClass: charClasses[0].tajweedClass,
    tooltip: charClasses[0].tooltip,
    wordIndex: charClasses[0].wordIndex,
  };

  for (let i = 1; i < charClasses.length; i++) {
    const cc = charClasses[i];
    if (
      cc.tajweedClass === cur.tajweedClass &&
      cc.tooltip === cur.tooltip &&
      cc.wordIndex === cur.wordIndex
    ) {
      cur.text += cc.char;
    } else {
      spans.push(cur);
      cur = {
        text: cc.char,
        tajweedClass: cc.tajweedClass,
        tooltip: cc.tooltip,
        wordIndex: cc.wordIndex,
      };
    }
  }
  spans.push(cur);
  return spans;
}

// ── Component ──────────────────────────────────────────────────────
const AyahDisplay: React.FC = () => {
  const ayah = useSelector((state: RootState) => state.ayah.current);
  const loading = useSelector((state: RootState) => state.ayah.loading);
  const error = useSelector((state: RootState) => state.ayah.error);
  const evaluation = useSelector((state: RootState) => state.evaluation.current);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animFrameRef = useRef<number>(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedReciter, setSelectedReciter] = useState(RECITERS[0].id);
  const [activeWordIndex, setActiveWordIndex] = useState(-1);
  const [audioDuration, setAudioDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [reciterOpen, setReciterOpen] = useState(false);
  // Color hints (rule highlights) — toggleable, persisted across sessions.
  // Findings/errors highlights are NOT affected by this toggle.
  const [showHints, setShowHints] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return localStorage.getItem('tajweed_show_hints') !== '0';
  });
  useEffect(() => {
    localStorage.setItem('tajweed_show_hints', showHints ? '1' : '0');
  }, [showHints]);
  const reciterRef = useRef<HTMLDivElement | null>(null);

  // Close reciter dropdown on outside click
  useEffect(() => {
    if (!reciterOpen) return;
    const handler = (e: MouseEvent) => {
      if (reciterRef.current && !reciterRef.current.contains(e.target as Node)) {
        setReciterOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [reciterOpen]);

  const audioUrl = useMemo(() => {
    if (!ayah) return null;
    const reciter = RECITERS.find((r) => r.id === selectedReciter) ?? RECITERS[0];
    return buildAudioUrl(reciter.folder, ayah.surah, ayah.ayah);
  }, [ayah, selectedReciter]);

  // Split text into words for karaoke
  const words = useMemo(() => {
    if (!ayah) return [];
    return splitIntoWords(ayah.referenceTextNormalized);
  }, [ayah]);

  // Build character classes + word spans
  const wordSpans = useMemo(() => {
    if (!ayah?.referenceTextNormalized) return [];
    const findings = evaluation?.findings ?? [];
    // When hints are off, skip rule-trigger highlights but keep findings/errors.
    const triggers = showHints ? (ayah.ruleTriggers ?? []) : [];
    const charClasses = buildCharClasses(
      ayah.referenceTextNormalized,
      triggers,
      findings,
      words
    );
    return groupIntoSpans(charClasses);
  }, [ayah, evaluation, words, showHints]);

  const presentRules = useMemo(() => {
    if (!ayah?.ruleTriggers) return [];
    const rules = new Set(ayah.ruleTriggers.map((t) => t.rule));
    return Array.from(rules) as TajweedRule[];
  }, [ayah]);

  // Karaoke: track current word based on audio time
  const updateKaraoke = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !audioDuration || words.length === 0) return;

    const progress = audio.currentTime / audioDuration;
    // Weight words by their character length for more natural timing
    const totalChars = words.reduce((sum, w) => sum + w.word.length, 0);
    let accumulated = 0;
    let idx = 0;
    for (let i = 0; i < words.length; i++) {
      accumulated += words[i].word.length / totalChars;
      if (progress < accumulated) {
        idx = i;
        break;
      }
      idx = i;
    }
    setActiveWordIndex(idx);

    if (!audio.paused) {
      animFrameRef.current = requestAnimationFrame(updateKaraoke);
    }
  }, [audioDuration, words]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;

    if (isPlaying) {
      audio.pause();
      cancelAnimationFrame(animFrameRef.current);
      setIsPlaying(false);
      setActiveWordIndex(-1);
    } else {
      audio.src = audioUrl;
      audio.playbackRate = playbackSpeed;
      audio.play().then(() => {
        setIsPlaying(true);
        animFrameRef.current = requestAnimationFrame(updateKaraoke);
      }).catch(() => setIsPlaying(false));
    }
  }, [isPlaying, audioUrl, updateKaraoke, playbackSpeed]);

  const handleAudioEnded = useCallback(() => {
    cancelAnimationFrame(animFrameRef.current);
    setIsPlaying(false);
    setActiveWordIndex(-1);
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  }, []);

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => cancelAnimationFrame(animFrameRef.current);
  }, []);

  // Reset karaoke on ayah/reciter change
  useEffect(() => {
    setActiveWordIndex(-1);
    setAudioDuration(0);
  }, [ayah, selectedReciter]);

  if (loading) {
    return (
      <div className="card flex items-center justify-center py-12">
        <svg className="animate-spin h-8 w-8 text-emerald-500" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card border-red-500/50">
        <p className="text-red-400 text-center">{error}</p>
      </div>
    );
  }

  if (!ayah) {
    return null;
  }

  const reciterName = RECITERS.find((r) => r.id === selectedReciter)?.name;
  const errorsCount = evaluation?.findings?.length ?? 0;

  return (
    <div className="card relative overflow-hidden">
      {/* Decorative gradient accent */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500" />
      <div className="pointer-events-none absolute -top-20 -right-20 w-64 h-64 rounded-full bg-emerald-100/40 dark:bg-emerald-500/5 blur-3xl" />

      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
              </svg>
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                {ayah.surahName}
              </h2>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                Ayah {ayah.ayah} of Surah {ayah.surah}
              </p>
            </div>
          </div>
        </div>

        {/* Reference Audio Player — refined */}
        <div className="relative mb-5 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100/80 dark:from-gray-800/40 dark:to-gray-800/20 border border-gray-200/80 dark:border-gray-700/50 p-3 shadow-sm">
          <audio
            ref={audioRef}
            onEnded={handleAudioEnded}
            onLoadedMetadata={handleLoadedMetadata}
            preload="none"
          />
          <div className="flex items-center gap-3">
            <button
              onClick={togglePlay}
              className={clsx(
                'relative flex items-center justify-center w-11 h-11 rounded-full transition-all duration-200 shrink-0 shadow-md',
                isPlaying
                  ? 'bg-gradient-to-br from-amber-400 to-amber-600 hover:from-amber-300 hover:to-amber-500 text-white shadow-amber-500/30'
                  : 'bg-gradient-to-br from-emerald-500 to-emerald-700 hover:from-emerald-400 hover:to-emerald-600 text-white shadow-emerald-500/30'
              )}
              title={isPlaying ? 'Pause reference' : 'Listen to reference recitation'}
            >
              {isPlaying && (
                <span className="absolute inset-0 rounded-full border-2 border-amber-400/40 animate-ping" />
              )}
              {isPlaying ? (
                <svg className="w-4 h-4 relative" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="6" y="5" width="4" height="14" rx="1" />
                  <rect x="14" y="5" width="4" height="14" rx="1" />
                </svg>
              ) : (
                <svg className="w-4 h-4 ml-0.5 relative" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>

            <div className="flex flex-col gap-1.5 min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] uppercase tracking-wider font-medium text-gray-500 dark:text-gray-400">
                  Reference
                </span>
                {isPlaying && (
                  <span className="flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400 font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                    Playing
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative" ref={reciterRef}>
                  <button
                    onClick={() => setReciterOpen(!reciterOpen)}
                    className="flex items-center gap-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 hover:border-emerald-400 dark:hover:border-emerald-600 transition-colors shadow-sm"
                  >
                    <svg className="w-3 h-3 text-emerald-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2a3 3 0 00-3 3v6a3 3 0 006 0V5a3 3 0 00-3-3z" />
                      <path d="M19 11a7 7 0 11-14 0 1 1 0 112 0 5 5 0 0010 0 1 1 0 112 0z" />
                      <path d="M12 18a1 1 0 011 1v2a1 1 0 11-2 0v-2a1 1 0 011-1z" />
                    </svg>
                    {reciterName}
                    <svg
                      className={clsx('w-3 h-3 transition-transform opacity-60', reciterOpen && 'rotate-180')}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {reciterOpen && (
                    <div className="absolute left-0 top-full mt-1 z-50 min-w-[160px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl py-1">
                      {RECITERS.map((r) => (
                        <button
                          key={r.id}
                          onClick={() => {
                            setSelectedReciter(r.id);
                            setReciterOpen(false);
                            if (isPlaying) {
                              audioRef.current?.pause();
                              cancelAnimationFrame(animFrameRef.current);
                              setIsPlaying(false);
                              setActiveWordIndex(-1);
                            }
                          }}
                          className={clsx(
                            'w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center gap-2',
                            r.id === selectedReciter
                              ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 font-medium'
                              : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          )}
                        >
                          {r.id === selectedReciter && (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                          <span className={r.id === selectedReciter ? '' : 'ml-5'}>{r.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* Arabic text — manuscript style with subtle decorative borders */}
        <div className="relative">
          {/* Decorative top frame */}
          <div className="flex items-center gap-3 mb-1 px-1">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-emerald-200 dark:via-emerald-800/40 to-emerald-300/60 dark:to-emerald-700/40" />
            <svg className="w-3 h-3 text-emerald-400/60" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" />
            </svg>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent via-emerald-200 dark:via-emerald-800/40 to-emerald-300/60 dark:to-emerald-700/40" />
          </div>

          <div className="arabic-text py-7 px-5 bg-gradient-to-b from-amber-50/30 via-white to-amber-50/20 dark:from-gray-800/30 dark:via-gray-800/40 dark:to-gray-800/30 rounded-2xl border border-amber-200/40 dark:border-gray-700/60 shadow-inner">
            {wordSpans.map((span, idx) => {
              const isActiveWord = isPlaying && span.wordIndex >= 0 && span.wordIndex === activeWordIndex;
              const isPastWord = isPlaying && span.wordIndex >= 0 && span.wordIndex < activeWordIndex;

              return (
                <span
                  key={idx}
                  className={clsx(
                    span.tajweedClass,
                    isActiveWord && 'arabic-karaoke-active',
                    isPastWord && 'arabic-karaoke-past',
                  )}
                  title={span.tooltip || undefined}
                >
                  {span.text}
                </span>
              );
            })}
          </div>

          {/* Decorative bottom frame */}
          <div className="flex items-center gap-3 mt-1 px-1">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-emerald-200 dark:via-emerald-800/40 to-emerald-300/60 dark:to-emerald-700/40" />
            <svg className="w-3 h-3 text-emerald-400/60" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" />
            </svg>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent via-emerald-200 dark:via-emerald-800/40 to-emerald-300/60 dark:to-emerald-700/40" />
          </div>
        </div>

        {/* Rule legend + status + hints toggle */}
        {(presentRules.length > 0 || errorsCount > 0) && (
          <div className="flex flex-wrap items-center gap-2 mt-4 justify-center">
            {presentRules.length > 0 && (
              <span className="text-[10px] uppercase tracking-wider font-medium text-gray-400 dark:text-gray-500 mr-1">
                Rules in this ayah:
              </span>
            )}
            {showHints && presentRules.map((rule) => (
              <span
                key={rule}
                className={clsx(
                  'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border',
                  RULE_LEGEND_COLOR[rule]
                )}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                {RULE_LABEL[rule]}
              </span>
            ))}
            {errorsCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-500/30">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errorsCount} {errorsCount === 1 ? 'Error' : 'Errors'} Found
              </span>
            )}
            {presentRules.length > 0 && (
              <button
                type="button"
                onClick={() => setShowHints((v) => !v)}
                title={showHints ? 'Hide color hints' : 'Show color hints'}
                aria-label={showHints ? 'Hide color hints' : 'Show color hints'}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                {showHints ? (
                  <>
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                    Hide hints
                  </>
                ) : (
                  <>
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Show hints
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AyahDisplay;
